import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Label from '../../components/ui/Label';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ErrorDialog from '../../components/ui/ErrorDialog';
import { FunctionalSelect, SelectItem } from '../../components/ui/Select';
import { FASTCUBE_ORG, organizationsService } from '../../services/organizationsService';
import { adminOrgsApi, apiErrorText, ORG_NAME_RULE, parsePublisherIds, sanitizeOrgName } from './adminOrgsApi';

const PUBLISHER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'with', label: 'With publishers' },
  { value: 'without', label: 'No publishers' },
];

const EMPTY_FORM = { name: '', description: '', publisher_ids: '', is_publisher: false };

/**
 * Organizations listing for platform admins: search, filters, a "New organization" modal and
 * one-click row actions. Name, publishers, properties and members live on the detail page.
 */
function AdminOrgsList() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [publisherFilter, setPublisherFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await adminOrgsApi.list();
      setOrgs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(apiErrorText(err, 'Failed to load organizations.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs
      .filter((o) => {
        const n = parsePublisherIds(o.publisher_ids).length;
        if (publisherFilter === 'with') return n > 0;
        if (publisherFilter === 'without') return n === 0;
        return true;
      })
      .filter((o) => !q || (o.name || '').toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orgs, search, publisherFilter]);

  const createOrg = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Enter an organization name.');
      return;
    }
    setCreating(true);
    try {
      const created = await adminOrgsApi.create({
        name,
        description: form.description.trim() || null,
        publisher_ids: parsePublisherIds(form.publisher_ids).join(',') || null,
        is_publisher: Boolean(form.is_publisher),
      });
      await organizationsService.resetOrganizations();
      toast.success(`${created.name} created`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      navigate(`/admin/organizations/${created.id}`);
    } catch (err) {
      setErrorMessage(apiErrorText(err, 'Failed to create organization.'));
    } finally {
      setCreating(false);
    }
  };

  const deactivate = (org) =>
    setConfirm({
      title: 'Deactivate organization',
      message: `Deactivate ${org.name}? This is blocked while it still has members.`,
      confirmText: 'Deactivate',
      run: async () => {
        setBusyId(org.id);
        try {
          await adminOrgsApi.deactivate(org.id);
          setOrgs((prev) => prev.filter((o) => o.id !== org.id));
          await organizationsService.resetOrganizations();
          toast.success(`${org.name} deactivated`);
        } catch (err) {
          setErrorMessage(apiErrorText(err, 'Failed to deactivate organization.'));
        } finally {
          setBusyId(null);
        }
      },
    });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>
            Organizations
            {!loading ? <span className="ml-2 text-sm font-normal text-zinc-500">({visible.length} of {orgs.length})</span> : null}
          </CardTitle>
          <Button variant="outline" size="icon" onClick={() => setCreateOpen(true)} aria-label="New organization" title="New organization">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label="Search organizations"
            />
          </div>
          <FunctionalSelect label="Publishers" className="w-52" value={publisherFilter} onValueChange={setPublisherFilter}>
            {PUBLISHER_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading organizations…</p>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">{error}</p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No organizations match.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="py-2 pr-3 font-medium">Organization</th>
                  <th className="py-2 pr-3 font-medium">Publishers</th>
                  <th className="py-2 pr-3 font-medium">Created</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((org) => {
                  const publishers = parsePublisherIds(org.publisher_ids);
                  const isFastCube = org.name === FASTCUBE_ORG;
                  return (
                    <tr key={org.id} className="border-b border-gray-100">
                      <td className="py-2 pr-3 align-top">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/organizations/${org.id}`)}
                          className="text-left font-medium text-[#18181B] hover:text-[#0062FB] hover:underline"
                        >
                          {org.name}
                        </button>
                        {isFastCube ? (
                          <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Platform</span>
                        ) : null}
                        {org.description ? <div className="text-xs text-zinc-500">{org.description}</div> : null}
                      </td>
                      <td className="py-2 pr-3 align-top text-zinc-600">
                        {isFastCube ? (
                          <span className="text-xs text-zinc-400">all publishers</span>
                        ) : publishers.length === 0 ? (
                          <span className="text-xs text-amber-700">none — sees no offers</span>
                        ) : (
                          <span title={publishers.join(', ')}>
                            {publishers.length} {publishers.length === 1 ? 'publisher' : 'publishers'}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 align-top text-zinc-600">
                        {org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 align-top text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/organizations/${org.id}`)}
                            className="rounded p-1.5 text-[#0062FB] hover:bg-blue-50"
                            title="Edit organization"
                            aria-label="Edit organization"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deactivate(org)}
                            disabled={isFastCube || busyId === org.id}
                            className="rounded p-1.5 text-[#F53D7A] hover:bg-pink-50 disabled:opacity-40"
                            title={isFastCube ? 'The platform organization cannot be deactivated' : 'Deactivate organization'}
                            aria-label="Deactivate organization"
                          >
                            {busyId === org.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Modal isOpen={createOpen} onClose={() => !creating && setCreateOpen(false)} onEnter={createOrg} className="w-full max-w-lg mx-4">
        <CardHeader className="pr-10">
          <CardTitle>New organization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: sanitizeOrgName(e.target.value) }))}
              placeholder="acme"
              disabled={creating}
              autoFocus
            />
            <p className="text-xs text-zinc-500">{ORG_NAME_RULE} Creating also sets up the organization's universe table in Databricks.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="org-desc">Description</Label>
            <Input id="org-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional" disabled={creating} />
          </div>
          <label htmlFor="org-is-publisher" className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-[#18181B]">
            <input
              id="org-is-publisher"
              type="checkbox"
              className="h-4 w-4 accent-[#0062FB]"
              checked={Boolean(form.is_publisher)}
              onChange={(e) => setForm((f) => ({ ...f, is_publisher: e.target.checked }))}
              disabled={creating}
            />
            Publisher
          </label>
          <div className="space-y-2">
            <Label htmlFor="org-pubs">Publisher IDs</Label>
            <Input
              id="org-pubs"
              value={form.publisher_ids}
              onChange={(e) => setForm((f) => ({ ...f, publisher_ids: e.target.value.replace(/[^0-9,\s]/g, '') }))}
              placeholder="12, 34, 56 (optional, can be added later)"
              disabled={creating}
            />
            <p className="text-xs text-zinc-500">Offer reports are restricted to these publishers; without any, the organization sees no offers.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button variant="outline" onClick={createOrg} disabled={creating || !form.name.trim()} className="gap-1.5">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </div>
        </CardContent>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'OK'}
        cancelText="Cancel"
      />
      <ErrorDialog isOpen={Boolean(errorMessage)} onClose={() => setErrorMessage('')} title="Error" message={errorMessage} />
    </Card>
  );
}

export default AdminOrgsList;
